/**
 * Archives Tab Component
 *
 * Matches original HTML structure: .archives-container, .archive-selector-bar,
 * .archive-all-songs-container, .archive-songs-grid, .card-rank-badge, etc.
 */

import { useState, useEffect } from 'preact/hooks';
import { selectedGroup } from '@/stores/groups';
import { isAuthenticated } from '@/stores/auth';
import { api } from '@/api/client';
import { play, currentTrackId, isPlaying } from '@/stores/player';
import type { ArchivePeriod, Song } from '@/types';

export function ArchivesTab() {
  const [periods, setPeriods] = useState<ArchivePeriod[]>([]);
  const [selectedMonth, setSelectedMonth] = useState('');
  const [periodSongs, setPeriodSongs] = useState<Song[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (selectedGroup.value && isAuthenticated.value) {
      fetchArchives();
    }
  }, [selectedGroup.value?.id]);

  const fetchArchives = async () => {
    if (!selectedGroup.value) return;

    setIsLoading(true);
    try {
      const response = await api.get<{ archives: ArchivePeriod[] }>(
        `/api/songs/archive/${selectedGroup.value.id}`
      );
      if (response.data?.archives) {
        setPeriods(response.data.archives);
        if (response.data.archives.length > 0) {
          const first = response.data.archives[0];
          setSelectedMonth(`${first.month}-${first.year}`);
        }
      }
    } catch (e) {
      console.error('Failed to fetch archives:', e);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPeriodSongs = async (month: string, year: number) => {
    if (!selectedGroup.value) return;

    try {
      const response = await api.get<{ songs: Song[] }>(
        `/api/songs/group/${selectedGroup.value.id}?month=${month}&year=${year}`
      );
      if (response.data?.songs) {
        const sorted = response.data.songs.sort((a, b) => b.votes.score - a.votes.score);
        setPeriodSongs(sorted);
      }
    } catch (e) {
      console.error('Failed to fetch period songs:', e);
    }
  };

  useEffect(() => {
    if (selectedMonth) {
      const [month, year] = selectedMonth.split('-');
      fetchPeriodSongs(month, parseInt(year));
    }
  }, [selectedMonth]);

  const handlePlaySong = (song: Song) => {
    play(song);
  };

  // Get rank badge class
  const getRankBadgeClass = (index: number): string => {
    if (index === 0) return 'card-rank-badge gold';
    if (index === 1) return 'card-rank-badge silver';
    if (index === 2) return 'card-rank-badge bronze';
    return 'card-rank-badge';
  };

  // Get rank text
  const getRankText = (index: number): string => {
    if (index === 0) return '1st';
    if (index === 1) return '2nd';
    if (index === 2) return '3rd';
    return `#${index + 1}`;
  };

  if (!isAuthenticated.value) {
    return (
      <div class="archives-container">
        <div class="welcome-section">
          <h2>Sign in to view archives</h2>
          <p>See past months' winners and rankings</p>
        </div>
      </div>
    );
  }

  if (!selectedGroup.value) {
    return (
      <div class="archives-container">
        <div class="welcome-section">
          <h2>Select a pod</h2>
          <p>Choose a pod to see its archives</p>
        </div>
      </div>
    );
  }

  return (
    <div class="archives-container">
      {/* Archive timeline selector */}
      <div class="archive-selector-bar">
        <div class="archive-selector">
          <label for="month-select">Month</label>
          <select
            id="month-select"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth((e.target as HTMLSelectElement).value)}
          >
            {periods.map((period) => (
              <option key={`${period.month}-${period.year}`} value={`${period.month}-${period.year}`}>
                {period.month} {period.year}
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Archive content */}
      <div class="archive-content">
        {isLoading ? (
          <div class="archive-loading">
            <div class="loading-spinner"></div>
            <p>Loading archives...</p>
          </div>
        ) : periods.length === 0 ? (
          <div class="no-archive-submissions">
            <div class="empty-state-icon">🎵</div>
            <h3>No archives yet</h3>
            <p>Past months' results will appear here</p>
          </div>
        ) : periodSongs.length === 0 ? (
          <div class="no-archive-submissions">
            <div class="empty-state-icon">🎵</div>
            <h3>No songs were submitted during {selectedMonth.replace('-', ' ')}</h3>
            <p>Check out another month from the selector above.</p>
          </div>
        ) : (
          <div class="archive-month-container">
            {/* Archive all songs container - matches original structure */}
            <div class="archive-all-songs-container">
              <h2 class="archive-songs-title">All Submissions</h2>
              <div class="archive-songs-grid">
                {periodSongs.map((song, index) => {
                  const isCurrentlyPlaying = currentTrackId.value === song.spotifyId && isPlaying.value;

                  return (
                    <div
                      key={song.id}
                      class={`song-card ${index === 0 ? 'winner' : ''} ${isCurrentlyPlaying ? 'playing' : ''}`}
                      data-spotify-id={song.spotifyId}
                      data-spotify-track-group="archives"
                    >
                      {/* Rank badge */}
                      <div class={getRankBadgeClass(index)}>
                        {index < 3 ? (
                          <>{getRankText(index).slice(0, -2)}<sup>{getRankText(index).slice(-2)}</sup></>
                        ) : (
                          getRankText(index)
                        )}
                      </div>

                      {/* Mobile floating likes */}
                      <div class="likes-pill mobile-floating-only">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                          <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"></path>
                          <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
                        </svg>
                        <span class="likes-count">{song.votes.score}</span>
                      </div>

                      {/* Song image */}
                      <div class="song-image" onClick={() => handlePlaySong(song)}>
                        <img
                          src={song.albumArt || '/api/placeholder/100/100'}
                          alt={song.album || song.title}
                          loading="lazy"
                        />
                        <div class="song-play-overlay">
                          {isCurrentlyPlaying ? (
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                            </svg>
                          ) : (
                            <svg width="32" height="32" viewBox="0 0 24 24" fill="currentColor">
                              <path d="M8 5v14l11-7z" />
                            </svg>
                          )}
                        </div>
                      </div>

                      {/* Song info */}
                      <div class="song-info">
                        <div class="song-title-wrapper">
                          <button class="song-title-play-btn" onClick={() => handlePlaySong(song)}>
                            {isCurrentlyPlaying ? (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
                              </svg>
                            ) : (
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M8 5v14l11-7z" />
                              </svg>
                            )}
                          </button>
                          <a
                            href={`https://open.spotify.com/track/${song.spotifyId}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            class="song-title spotify-content-link"
                          >
                            {song.title}
                          </a>
                        </div>

                        <p class="song-artist">{song.artist}</p>
                        {song.album && <p class="song-album">{song.album}</p>}

                        <div class="song-meta-stacked">
                          <div class="submitter-info">
                            {song.userAvatar ? (
                              <img src={song.userAvatar} alt="" class="submitter-avatar" />
                            ) : (
                              <div class="submitter-avatar submitter-avatar-fallback">
                                {song.userName.charAt(0).toUpperCase()}
                              </div>
                            )}
                            <span class="submitter-name">{song.userName}</span>
                          </div>
                          <div class="likes-pill">
                            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                              <path d="M14 9V5a3 3 0 0 0-3-3l-4 9v11h11.28a2 2 0 0 0 2-1.7l1.38-9a2 2 0 0 0-2-2.3H14z"></path>
                              <path d="M7 22H4a2 2 0 0 1-2-2v-7a2 2 0 0 1 2-2h3"></path>
                            </svg>
                            <span class="likes-count">{song.votes.score}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
